/**
 * Service to import conversations from JaPlanchers API into Supabase
 * Maps external API format to local database schema
 */

import { supabase } from '../lib/supabase';
import { JaPlanchersAPI, JaPlanchersConversation, JaPlanchersCustomer, JaPlanchersMessage } from './JaPlanchersAPI';

export interface ImportProgress {
  total: number;
  processed: number;
  imported: number;
  skipped: number;
  failed: number;
  currentConversation?: string;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export class ConversationImporter {
  private jaPlanchersAPI: JaPlanchersAPI;

  constructor(apiKey: string) {
    this.jaPlanchersAPI = new JaPlanchersAPI(apiKey);
  }

  /**
   * Import recent conversations from JaPlanchers into Supabase
   * @param limit Number of conversations to fetch
   * @param onProgress Callback for progress updates
   */
  async importRecentConversations(
    limit: number = 50,
    onProgress?: (progress: ImportProgress) => void
  ): Promise<ImportResult> {
    console.log('[ConversationImporter] Starting import...');

    const result: ImportResult = {
      success: false,
      imported: 0,
      skipped: 0,
      failed: 0,
      errors: [],
    };

    try {
      // Fetch conversations from JaPlanchers API
      const conversations = await this.jaPlanchersAPI.fetchRecentConversations(limit);

      const progress: ImportProgress = {
        total: conversations.length,
        processed: 0,
        imported: 0,
        skipped: 0,
        failed: 0,
      };

      // Import each conversation
      for (const conv of conversations) {
        progress.currentConversation = `${conv.customer.full_name} (ID: ${conv.id})`;
        onProgress?.(progress);

        try {
          const imported = await this.importConversation(conv);
          if (imported) {
            progress.imported++;
            result.imported++;
          } else {
            progress.skipped++;
            result.skipped++;
          }
        } catch (error) {
          progress.failed++;
          result.failed++;
          const errorMsg = `Failed to import conversation ${conv.id}: ${error instanceof Error ? error.message : String(error)}`;
          result.errors.push(errorMsg);
          console.error('[ConversationImporter]', errorMsg);
        }

        progress.processed++;
        onProgress?.(progress);
      }

      result.success = result.failed === 0;
      console.log('[ConversationImporter] Import complete:', result);
      return result;

    } catch (error) {
      const errorMsg = `Import failed: ${error instanceof Error ? error.message : String(error)}`;
      result.errors.push(errorMsg);
      console.error('[ConversationImporter]', errorMsg);
      return result;
    }
  }

  /**
   * Import a single conversation
   * @returns True if imported, false if skipped (duplicate)
   */
  private async importConversation(conv: JaPlanchersConversation): Promise<boolean> {
    // 1. Check if conversation already exists (by external ID)
    const { data: existing } = await supabase
      .from('conversations')
      .select('id')
      .eq('external_id', String(conv.id))
      .single();

    if (existing) {
      console.log(`[ConversationImporter] Conversation ${conv.id} already exists, skipping`);
      return false;
    }

    // 2. Upsert client
    const clientId = await this.upsertClient(conv.customer);
    if (!clientId) {
      throw new Error('Failed to create/find client');
    }

    // 3. Create conversation record
    const { data: conversation, error: convError } = await supabase
      .from('conversations')
      .insert({
        external_id: String(conv.id),
        client_id: clientId,
        status: conv.status === 'active' ? 'active' : 'archived',
        channel: 'sms', // JaPlanchers is SMS-based
        channel_id: String(conv.id),
        created_at: conv.created_at || new Date().toISOString(),
        updated_at: conv.last_message_at || new Date().toISOString(),
      })
      .select('id')
      .single();

    if (convError || !conversation) {
      throw new Error(`Failed to create conversation: ${convError?.message}`);
    }

    // 4. Import all messages
    await this.importMessages(conversation.id, conv.messages);

    console.log(`[ConversationImporter] Imported conversation ${conv.id} with ${conv.messages.length} messages`);
    return true;
  }

  /**
   * Upsert client into clients table
   * @returns Client UUID
   */
  private async upsertClient(customer: JaPlanchersCustomer): Promise<string | null> {
    // Try to find existing client by phone or email
    let existingClient = null;

    if (customer.phone) {
      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('phone', customer.phone)
        .single();
      existingClient = data;
    }

    if (!existingClient && customer.email) {
      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('email', customer.email)
        .single();
      existingClient = data;
    }

    if (existingClient) {
      return existingClient.id;
    }

    // Create new client
    const { data: newClient, error } = await supabase
      .from('clients')
      .insert({
        name: customer.full_name,
        email: customer.email,
        phone: customer.phone,
        external_id: String(customer.id),
      })
      .select('id')
      .single();

    if (error) {
      console.error('[ConversationImporter] Failed to create client:', error);
      console.error('[ConversationImporter] Client data:', {
        name: customer.full_name,
        email: customer.email,
        phone: customer.phone,
        external_id: customer.id
      });
      throw new Error(`Failed to create client: ${error.message} (${error.code})`);
    }

    return newClient?.id || null;
  }

  /**
   * Import messages for a conversation
   */
  private async importMessages(
    conversationId: string,
    messages: JaPlanchersMessage[]
  ): Promise<void> {
    if (messages.length === 0) return;

    // Map messages to Supabase format
    const messagesToInsert = messages.map(msg => ({
      conversation_id: conversationId,
      sender_type: msg.direction === 'inbound' ? 'client' : 'bot',
      content: msg.body,
      external_id: msg.twilio_sid,
      created_at: msg.sent_at || msg.created_at || new Date().toISOString(),
      metadata: {
        from_phone: msg.from_phone,
        to_phone: msg.to_phone,
        status: msg.status,
      },
    }));

    // Batch insert messages
    const { error } = await supabase
      .from('messages')
      .insert(messagesToInsert);

    if (error) {
      throw new Error(`Failed to insert messages: ${error.message}`);
    }
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<boolean> {
    return this.jaPlanchersAPI.testConnection();
  }
}
